import { logger } from '@utils/safeLogger'
import {
  IFolder,
  ICreateFolderRequest,
  IUpdateFolderRequest,
  IMoveStudyRequest,
  IFile,
  IUpdateFileRequest,
  IMoveFileRequest,
  IWorkspace,
  IFileDetails,
  IFolderDetails,
  IPreviewPayload,
  IDownloadPayload,
  IFolderZipEstimate,
} from '@dataTypes/folder.types'
import axios from './axiosConfig'

import { getEnv } from '@utils/env'
import { getAccessToken } from '@services/auth/accessTokenStore'
import { toApiError } from './errors/api-error'

// Define the types for the response structure
export type GetPatientFolderResponse = {
  statusCode: number
  message: string
  data: {
    folder?: IFolder
  }
}

export type GetFolderByIdResponse = {
  statusCode: number
  message: string
  data: {
    folder?: IFolder
  }
}

export type CreateFolderResponse = {
  statusCode: number
  message: string
  data: IFolder
}

export type UpdateFolderResponse = {
  statusCode: number
  message: string
  data: IFolder
}

export type MoveStudyResponse = {
  statusCode: number
  message: string
  data: any
}

export type UploadFileResponse = {
  statusCode: number
  message: string
  data: IFile
}

export type DeleteFileResponse = {
  statusCode: number
  message: string
  data: Record<string, unknown>
}

export type UpdateFileResponse = {
  statusCode: number
  message: string
  data: IFile
}

export type GetTotalStorageResponse = {
  statusCode: number
  message: string
  data: {
    totalSize: number
  }
}

type GenericResponse = {
  statusCode: number
  message: string
  data: Record<string, unknown>
}

type WorkspaceResponse = {
  statusCode: number
  message: string
  data: IWorkspace
}

type FileDetailsResponse = {
  statusCode: number
  message: string
  data: IFileDetails
}

type FolderDetailsResponse = {
  statusCode: number
  message: string
  data: IFolderDetails
}

type PatientStorageResponse = {
  statusCode: number
  message: string
  data: {
    patient_id: string
    totalSize: number
  }
}

type FilePreviewResponse = {
  statusCode: number
  message: string
  data: IPreviewPayload
}

type FileThumbnailResponse = {
  statusCode: number
  message: string
  data: { url: string | null }
}

type FileDownloadResponse = {
  statusCode: number
  message: string
  data: IDownloadPayload
}

type FolderZipEstimateResponse = {
  statusCode: number
  message: string
  data: IFolderZipEstimate
}

type UploadFilesResponse = {
  statusCode: number
  message: string
  data: {
    files: IFile[]
  }
}

type BulkFileOperationResponse = {
  statusCode: number
  message: string
  data: {
    operation: 'move' | 'delete' | 'favorite_on' | 'favorite_off'
    requested_count: number
    processed_count: number
    failed_count: number
    failures: Array<{ file_id: string; message: string }>
  }
}

const BUSINESS_LAYER_API =
  getEnv('REACT_APP_BUSINESS_LAYER_FOLDER') ||
  getEnv('REACT_APP_BUSINESS_LAYER_API')

const headers = {
  'Content-Type': 'application/json',
}

const uploadHeaders = {
  'Content-Type': 'multipart/form-data',
}

const ROOT_FOLDER_ID = 'root'

const appendPatientFileUploadFields = (
  formData: FormData,
  folderId?: string,
): void => {
  const clinicId = localStorage.getItem('clinic_id') || ''
  if (clinicId) {
    formData.append('clinic_id', clinicId)
  }
  if (folderId && folderId !== ROOT_FOLDER_ID) {
    formData.append('folder_id', folderId)
  }
}

const buildAuthHeaders = (
  extra: Record<string, string> = {},
): Record<string, string> => {
  const token = getAccessToken() || ''
  return {
    ...headers,
    ...extra,
    Authorization: `Bearer ${token}`,
  }
}

// Function to get patient folder (single folder for the patient)
export const getPatientFolder = async (
  patient_id: string,
): Promise<GetPatientFolderResponse> => {
  try {
    const token = getAccessToken() || ''
    const response = await axios.get<GetPatientFolderResponse>(
      `${BUSINESS_LAYER_API}/api/v1/folder/patient/folder`,
      {
        params: { patient_id },
        headers: {
          ...headers,
          Authorization: `Bearer ${token}`,
        },
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

// Function to get folder by ID
export const getFolderById = async (
  id: string,
): Promise<GetFolderByIdResponse> => {
  try {
    const token = getAccessToken() || ''
    const response = await axios.get<GetFolderByIdResponse>(
      `${BUSINESS_LAYER_API}/api/v1/folder/${id}`,
      {
        headers: {
          ...headers,
          Authorization: `Bearer ${token}`,
        },
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

// Function to create a new folder
export const createFolder = async (
  folderData: ICreateFolderRequest,
): Promise<CreateFolderResponse> => {
  try {
    const token = getAccessToken() || ''
    const response = await axios.post<CreateFolderResponse>(
      `${BUSINESS_LAYER_API}/api/v1/folder`,
      folderData,
      {
        headers: {
          ...headers,
          Authorization: `Bearer ${token}`,
        },
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

// Function to update folder
export const updateFolderById = async (
  id: string,
  updates: Partial<IUpdateFolderRequest>,
): Promise<UpdateFolderResponse> => {
  try {
    const token = getAccessToken() || ''
    const response = await axios.put<UpdateFolderResponse>(
      `${BUSINESS_LAYER_API}/api/v1/folder`,
      { folder_id: id, data: updates },
      {
        headers: {
          ...headers,
          Authorization: `Bearer ${token}`,
        },
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

// Function to delete folder
export const deleteFolderById = async (
  id: string,
): Promise<GenericResponse> => {
  try {
    const token = getAccessToken() || ''
    const response = await axios.delete<GenericResponse>(
      `${BUSINESS_LAYER_API}/api/v1/folder/${id}`,
      {
        headers: {
          ...headers,
          Authorization: `Bearer ${token}`,
        },
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

// Function to move study to folder
export const moveStudyToFolder = async (
  moveData: IMoveStudyRequest,
): Promise<MoveStudyResponse> => {
  try {
    const token = getAccessToken() || ''
    const response = await axios.post<MoveStudyResponse>(
      `${BUSINESS_LAYER_API}/api/v1/folder/move-study`,
      moveData,
      {
        headers: {
          ...headers,
          Authorization: `Bearer ${token}`,
        },
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

// Function to rename folder
export const renameFolder = async (
  folderId: string,
  newName: string,
): Promise<UpdateFolderResponse> => {
  try {
    const token = getAccessToken() || ''
    const response = await axios.put<UpdateFolderResponse>(
      `${BUSINESS_LAYER_API}/api/v1/folder/rename`,
      { folder_id: folderId, new_name: newName },
      {
        headers: {
          ...headers,
          Authorization: `Bearer ${token}`,
        },
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const moveFolder = async (
  folderId: string,
  targetParentFolderId: string | null,
): Promise<UpdateFolderResponse> => {
  try {
    const response = await axios.put<UpdateFolderResponse>(
      `${BUSINESS_LAYER_API}/api/v1/folder/move`,
      {
        folder_id: folderId,
        target_parent_folder_id: targetParentFolderId,
      },
      {
        headers: buildAuthHeaders(),
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const copyFolder = async (
  folderId: string,
  newName?: string,
): Promise<CreateFolderResponse> => {
  try {
    const response = await axios.post<CreateFolderResponse>(
      `${BUSINESS_LAYER_API}/api/v1/folder/${folderId}/copy`,
      newName ? { new_name: newName } : {},
      {
        headers: buildAuthHeaders(),
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

// Function to upload file
export const uploadFile = async (
  file: File,
  patientId: string,
  folderId?: string,
  onUploadProgress?: (progressEvent: {
    loaded: number
    total?: number
  }) => void,
): Promise<UploadFileResponse> => {
  try {
    const token = getAccessToken() || ''
    const formData = new FormData()

    formData.append('dicomFile', file)
    formData.append('patient_id', patientId)
    appendPatientFileUploadFields(formData, folderId)
    // Forward the source file's last-modified timestamp so JPG/PNG → DICOM
    // conversion can stamp each file with its own StudyDate/StudyTime.
    // Without this, all images uploaded on the same day share the upload-day
    // date and the OHIF viewport overlay never updates when switching files.
    if (typeof file.lastModified === 'number' && file.lastModified > 0) {
      formData.append('file_last_modified', String(file.lastModified))
    }

    const response = await axios.post<UploadFileResponse>(
      `${BUSINESS_LAYER_API}/api/v1/file`,
      formData,
      {
        headers: {
          ...uploadHeaders,
          Authorization: `Bearer ${token}`,
        },
        onUploadProgress,
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const uploadFiles = async (
  files: File[],
  patientId: string,
  folderId?: string,
): Promise<UploadFilesResponse> => {
  try {
    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))
    formData.append('patient_id', patientId)
    appendPatientFileUploadFields(formData, folderId)

    const response = await axios.post<UploadFilesResponse>(
      `${BUSINESS_LAYER_API}/api/v1/file/upload`,
      formData,
      {
        headers: buildAuthHeaders(uploadHeaders),
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

// Function to delete file
export const deleteFile = async (
  fileId: string,
): Promise<DeleteFileResponse> => {
  try {
    const token = getAccessToken() || ''
    const response = await axios.delete<DeleteFileResponse>(
      `${BUSINESS_LAYER_API}/api/v1/file/${fileId}`,
      {
        headers: {
          ...headers,
          Authorization: `Bearer ${token}`,
        },
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateFile = async (
  fileId: string,
  data: IUpdateFileRequest['data'],
): Promise<UpdateFileResponse> => {
  try {
    const response = await axios.put<UpdateFileResponse>(
      `${BUSINESS_LAYER_API}/api/v1/file`,
      {
        file_id: fileId,
        data,
      },
      {
        headers: buildAuthHeaders(),
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const moveFile = async (
  moveData: IMoveFileRequest,
): Promise<UpdateFileResponse> => {
  try {
    const response = await axios.post<UpdateFileResponse>(
      `${BUSINESS_LAYER_API}/api/v1/file/move`,
      moveData,
      {
        headers: buildAuthHeaders(),
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const copyFile = async (fileId: string): Promise<UpdateFileResponse> => {
  try {
    const response = await axios.post<UpdateFileResponse>(
      `${BUSINESS_LAYER_API}/api/v1/file/${fileId}/copy`,
      {},
      {
        headers: buildAuthHeaders(),
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const bulkFileOperation = async (payload: {
  patient_id: string
  file_ids: string[]
  operation: 'move' | 'delete' | 'favorite_on' | 'favorite_off'
  folder_id?: string
}): Promise<BulkFileOperationResponse> => {
  try {
    const response = await axios.post<BulkFileOperationResponse>(
      `${BUSINESS_LAYER_API}/api/v1/file/bulk`,
      payload,
      {
        headers: buildAuthHeaders(),
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

// Function to toggle file favorite status
export const toggleFileFavorite = async (
  fileId: string,
  isFavorite: boolean,
): Promise<UpdateFileResponse> => {
  try {
    const response = await axios.patch<UpdateFileResponse>(
      `${BUSINESS_LAYER_API}/api/v1/file/${fileId}/favorite`,
      { is_favorite: isFavorite },
      {
        headers: buildAuthHeaders(),
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

// Function to update file stats (download count, last accessed)
export const updateFileStats = async (
  fileId: string,
): Promise<GenericResponse> => {
  try {
    const token = getAccessToken() || ''
    const response = await axios.put<GenericResponse>(
      `${BUSINESS_LAYER_API}/api/v1/file/${fileId}/stats`,
      {},
      {
        headers: {
          ...headers,
          Authorization: `Bearer ${token}`,
        },
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}
// Function to download DICOM file from Azure DICOM service
// Function to get total storage
export const getTotalStorage = async (): Promise<GetTotalStorageResponse> => {
  try {
    const token = getAccessToken() || ''
    const response = await axios.get<GetTotalStorageResponse>(
      `${BUSINESS_LAYER_API}/api/v1/file/storage/total`,
      {
        headers: {
          ...headers,
          Authorization: `Bearer ${token}`,
        },
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getPatientStorage = async (
  patientId: string,
): Promise<PatientStorageResponse> => {
  try {
    const response = await axios.get<PatientStorageResponse>(
      `${BUSINESS_LAYER_API}/api/v1/file/storage/patient/${patientId}`,
      {
        headers: buildAuthHeaders(),
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getWorkspace = async (params: {
  patient_id: string
  folder_id?: string
  search?: string
  types?: string
  favorite?: 'true' | 'false'
  sort_by?: 'name' | 'date' | 'type'
  sort_dir?: 'asc' | 'desc'
  page?: number
  limit?: number
}): Promise<WorkspaceResponse> => {
  try {
    const response = await axios.get<WorkspaceResponse>(
      `${BUSINESS_LAYER_API}/api/v1/file/workspace`,
      {
        params,
        headers: buildAuthHeaders(),
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getFileDetails = async (
  fileId: string,
): Promise<FileDetailsResponse> => {
  try {
    const response = await axios.get<FileDetailsResponse>(
      `${BUSINESS_LAYER_API}/api/v1/file/${fileId}/details`,
      {
        headers: buildAuthHeaders(),
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getFolderDetails = async (
  folderId: string,
): Promise<FolderDetailsResponse> => {
  try {
    const response = await axios.get<FolderDetailsResponse>(
      `${BUSINESS_LAYER_API}/api/v1/folder/${folderId}/details`,
      {
        headers: buildAuthHeaders(),
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getFilePreviewUrl = async (
  fileId: string,
): Promise<FilePreviewResponse> => {
  try {
    const response = await axios.get<FilePreviewResponse>(
      `${BUSINESS_LAYER_API}/api/v1/file/${fileId}/preview-url`,
      {
        headers: buildAuthHeaders(),
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getFileThumbnailUrl = async (
  fileId: string,
): Promise<FileThumbnailResponse> => {
  try {
    const response = await axios.get<FileThumbnailResponse>(
      `${BUSINESS_LAYER_API}/api/v1/file/${fileId}/thumbnail-url`,
      {
        headers: buildAuthHeaders(),
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getFileDownloadUrl = async (
  fileId: string,
): Promise<FileDownloadResponse> => {
  try {
    const response = await axios.get<FileDownloadResponse>(
      `${BUSINESS_LAYER_API}/api/v1/file/${fileId}/download-url`,
      {
        headers: buildAuthHeaders(),
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getFolderZipEstimate = async (
  folderId: string,
): Promise<FolderZipEstimateResponse> => {
  try {
    const response = await axios.get<FolderZipEstimateResponse>(
      `${BUSINESS_LAYER_API}/api/v1/file/folder/${folderId}/download-zip-estimate`,
      {
        headers: buildAuthHeaders(),
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const downloadFolderZip = async (folderId: string): Promise<Blob> => {
  const response = await axios.get(
    `${BUSINESS_LAYER_API}/api/v1/file/folder/${folderId}/download-zip`,
    {
      headers: buildAuthHeaders(),
      responseType: 'blob',
    },
  )
  return response.data
}

// ---------- Claim attachment ----------

export const attachFileToClaim = async (
  fileId: string,
  claimId: string,
  type: string = '',
): Promise<{ statusCode: number; message: string; data: unknown }> => {
  try {
    const response = await axios.post(
      `${BUSINESS_LAYER_API}/api/v1/file/${fileId}/attach-claim`,
      { claim_id: claimId, type },
      { headers: buildAuthHeaders() },
    )
    return response.data
  } catch (error) {
    throw toApiError(error, 'An unknown error occurred')
  }
}

export const attachFileToClaimItem = async (
  fileId: string,
  claimItemId: string,
  type: string = '',
): Promise<{ statusCode: number; message: string; data: unknown }> => {
  try {
    const response = await axios.post(
      `${BUSINESS_LAYER_API}/api/v1/file/${fileId}/attach-claim-item`,
      { claim_item_id: claimItemId, type },
      { headers: buildAuthHeaders() },
    )
    return response.data
  } catch (error) {
    throw toApiError(error, 'An unknown error occurred')
  }
}

export type GetXrayStudiesResponse = {
  statusCode: number
  message: string
  data: {
    files: IFile[]
  }
}

export const getPatientXrayStudies = async (
  patientId: string,
  planId?: string,
): Promise<GetXrayStudiesResponse> => {
  try {
    const params: Record<string, string> = { patient_id: patientId }
    if (planId) params.plan_id = planId

    const response = await axios.get<GetXrayStudiesResponse>(
      `${BUSINESS_LAYER_API}/api/v1/folder/patient/xray-studies`,
      {
        params,
        headers: buildAuthHeaders(),
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const downloadDicomFile = async (file: IFile): Promise<void> => {
  try {
    const token = getAccessToken() || ''
    const tenantId = localStorage.getItem('tenant_id') || ''

    // DICOM Web module runs on port 5004
    const DICOM_WEB_API = getEnv(
      'REACT_APP_EXTERNAL_LAYER_DICOM',
      'http://localhost:5004',
    )

    const response = await axios.get(
      `${DICOM_WEB_API}/api/v1/external/dicom-web/studies/${file.study_uid}/series/${file.series_uid}/instances/${file.sop_uid}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': tenantId,
        },
        responseType: 'blob', // Important for binary data
      },
    )

    // Create blob URL and trigger download
    const blob = new Blob([response.data], { type: 'application/dicom' })
    const url = window.URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = file.file_name || file.original_name || 'dicom-file.dcm'
    link.target = '_blank'

    // Append to body, click, and remove
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up the blob URL
    window.URL.revokeObjectURL(url)
  } catch (error) {
    logger.error('Failed to download DICOM file from Azure:', error)
  }
}

/**
 * Delete a DICOM study directly from the DICOMweb service. Use this only as a
 * cleanup path when there is no matching PMS file record (e.g. the database
 * was reseeded but the DICOM store still holds the old studies). Normal
 * deletion should go through `deleteFile` so DB rows are cleaned up too.
 */
export const deleteOrphanDicomStudy = async (
  studyUid: string,
  seriesUid?: string,
): Promise<{ statusCode: number; message: string }> => {
  try {
    const token = getAccessToken() || ''
    const tenantId = localStorage.getItem('tenant_id') || ''

    const DICOM_WEB_API = getEnv(
      'REACT_APP_EXTERNAL_LAYER_DICOM',
      'http://localhost:5004',
    )

    const url = seriesUid
      ? `${DICOM_WEB_API}/api/v1/external/dicom-web/studies/${studyUid}/series/${seriesUid}`
      : `${DICOM_WEB_API}/api/v1/external/dicom-web/studies/${studyUid}`

    const response = await axios.delete(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Tenant-ID': tenantId,
      },
    })
    return {
      statusCode: response.status,
      message: 'Deleted from DICOM service',
    }
  } catch (e: any) {
    throw toApiError(e, 'Failed to delete')
  }
}
